import type { ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { useT } from '@kmgestao/i18n';
import { AppShell, Button, ThemeToggle, type AppShellNavItem } from '@kmgestao/ui';

import { Brand } from '../../components/Brand.js';
import { useSignOut } from '../../features/auth/index.js';

export interface SuperadminShellProps {
  children: ReactNode;
}

/**
 * Shell padrão das rotas Superadmin. Extende o `AppShell` do design system
 * com a navegação fixa do Superadmin. Use nas rotas internas — evita
 * duplicar navItems e `topbarRight` em cada página.
 */
export function SuperadminShell({ children }: SuperadminShellProps) {
  const { t } = useT('ai-admin');
  const { t: tCommon } = useT('common');
  const location = useLocation();
  const signOut = useSignOut();

  const navItems: AppShellNavItem[] = [
    {
      id: 'dashboard',
      label: t('nav.dashboard'),
      href: '/dashboard',
      active: location.pathname === '/dashboard',
    },
    {
      id: 'ai-dashboard',
      label: t('nav.aiDashboard'),
      href: '/superadmin/dashboard',
      active:
        location.pathname === '/superadmin/dashboard' ||
        location.pathname.startsWith('/superadmin/dashboard/'),
    },
    {
      id: 'dashboards',
      label: t('nav.dashboards', { defaultValue: 'Dashboards' }),
      href: '/superadmin/dashboards',
      active: location.pathname.startsWith('/superadmin/dashboards'),
    },
    {
      id: 'ai-settings',
      label: t('nav.aiSettings'),
      href: '/superadmin/settings/ai',
      active: location.pathname.startsWith('/superadmin/settings/ai'),
    },
    {
      id: 'platform-settings',
      label: t('nav.platformSettings', { defaultValue: 'Plataforma' }),
      href: '/superadmin/settings/platform',
      active: location.pathname.startsWith('/superadmin/settings/platform'),
    },
    {
      id: 'plans',
      label: t('nav.plans', { defaultValue: 'Planos' }),
      href: '/superadmin/billing/plans',
      active: location.pathname.startsWith('/superadmin/billing/plans'),
    },
    {
      id: 'leads',
      label: t('nav.leads'),
      href: '/superadmin/leads',
      active: location.pathname.startsWith('/superadmin/leads'),
    },
    {
      id: 'clinics',
      label: t('nav.clinics'),
      href: '/superadmin/clinics',
      active: location.pathname.startsWith('/superadmin/clinics'),
    },
    { id: 'billing', label: t('nav.billing'), href: '#' },
  ];

  return (
    <AppShell
      brand={<Brand />}
      brandSidebar={<Brand onDark />}
      navItems={navItems}
      topbarRight={
        <>
          <ThemeToggle />
          <Button
            size="sm"
            variant="outline"
            onClick={() => signOut.mutate()}
            disabled={signOut.isPending}
          >
            {signOut.isPending ? tCommon('status.loading', { defaultValue: 'Saindo…' }) : 'Sair'}
          </Button>
        </>
      }
      sidebarFooter={<p className="text-xs text-sidebar-foreground/60">v0.0.0 · dev build</p>}
    >
      {children}
    </AppShell>
  );
}
